import { Button, Checkbox, Group, NumberInput, Select, Stack, Tabs, Text, Textarea, TextInput } from '@mantine/core'
// import { Dropzone } from '@mantine/dropzone'
import { useForm } from '@mantine/form'
import { useDebouncedValue } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
// import { IconDownload, IconFileSpreadsheet, IconUpload, IconUserPlus, IconX } from '@tabler/icons-react'
import { IconEye } from '@tabler/icons-react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

// import { theme } from '@/configs/theme'
import { EmailPreview } from '@/components/EmailPreview'
import { useGlobalState } from '@/hooks/useGlobalState'
import { useIsSwiAdmin } from '@/hooks/useIsSwiAdmin'
import { useSetupProgressTracker } from '@/hooks/useSetupProgressTracker'
import type { UserRole } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'

import { MAX_TRIAL_DAYS, MIN_TRIAL_DAYS, TRIAL_DAYS, TRIAL_DURATION_LABEL } from './trial'
import type { UserFormValues } from './types'
import { validateEmail, validatePassword } from './utils'

// Create a subset type for the roles allowed in this modal
export type AllowedUserRole = Extract<UserRole, 'SME' | 'User' | 'Admin'>

interface AddUserModalProps {
  closeModal: () => void
  role?: AllowedUserRole
  entityName: string
}

/**
 * Resolves the trial duration sent to the backend. For non-SWI inviters we
 * always use the default. SWI Admins may override; we coerce, clamp to the
 * allowed bounds, and fall back to the default for any garbage value.
 */
function resolveTrialDays(isSwiAdmin: boolean, input: UserFormValues['trialDays']): number {
  if (!isSwiAdmin) return TRIAL_DAYS
  const parsed = typeof input === 'number' ? input : Number(input)
  if (!Number.isFinite(parsed)) return TRIAL_DAYS
  return Math.min(Math.max(Math.trunc(parsed), MIN_TRIAL_DAYS), MAX_TRIAL_DAYS)
}

export function AddUserModal({ closeModal, role, entityName }: AddUserModalProps) {
  const { api, uiStore } = useGlobalState()
  const isSwiAdmin = useIsSwiAdmin()
  const [isProcessing, setIsProcessing] = useState(false)

  // Get setup progress tracker for marking setup steps complete
  const { markStepComplete } = useSetupProgressTracker()
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [emailPreview, setEmailPreview] = useState<{
    recipientEmails: string[]
    body: string
    subject: string
    sourceEmail: string
  } | null>(null)
  // const [uploadedFile, setUploadedFile] = useState<File | null>(null)

  const canSelectRole = role == undefined

  const form = useForm<UserFormValues>({
    initialValues: {
      name: '',
      email: '',
      password: '',
      role: role ? role : 'User',
      topicIds: [],
      personalMessage: '',
      enableTrial: false,
      trialDays: TRIAL_DAYS,
    },
    validateInputOnBlur: ['email'],
    validate: {
      email: value => {
        const formatError = validateEmail(value)
        if (formatError) return formatError
        if (isDuplicate && value.toLowerCase() === debouncedEmail.toLowerCase())
          return 'User already exists. You can re-invite them directly or delete and re-add them.'
        return null
      },
      password: validatePassword,
    },
  })

  const [debouncedEmail] = useDebouncedValue(form.values.email, 500)
  const isValidFormat = !validateEmail(debouncedEmail)

  const { data: duplicateCheckResult } = useQuery({
    queryKey: ['users', 'duplicate-check', debouncedEmail],
    queryFn: () =>
      api.getUsers({
        page: 1,
        pageSize: 10,
        filterBy: debouncedEmail,
        isUserDisabled: false,
      }),
    enabled: !!debouncedEmail && isValidFormat,
    staleTime: 30_000,
  })

  const isDuplicate =
    !!debouncedEmail &&
    isValidFormat &&
    !!duplicateCheckResult?.members.some(
      m => m.email.toLowerCase() === debouncedEmail.toLowerCase() && m.status !== 'disabled',
    )

  useEffect(() => {
    if (form.values.email) form.validateField('email')
  }, [isDuplicate])

  // Trial only applies to End Users; clear the flag when role changes off User.
  useEffect(() => {
    if (form.values.role !== 'User' && form.values.enableTrial) {
      form.setFieldValue('enableTrial', false)
    }
  }, [form.values.role])

  const createUserMutation = useMutation({
    mutationFn: api.createUser,
    onMutate: () => uiStore.showLoadingOverlay(),
    onSettled: () => {
      uiStore.hideLoadingOverlay()
      setIsProcessing(false)
    },
    onSuccess: (d, _vars) => {
      const success = 'success' in d && d.success
      const error = 'errors' in d
      if (success) {
        api.queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
        api.queryClient.invalidateQueries({ queryKey: ['billing', 'info'] })
        api.queryClient.invalidateQueries({ queryKey: ['sponsor', 'info'] })

        // Mark inviteUsers step as complete when a user is invited
        if (role === 'User') {
          markStepComplete('inviteUsers')
        }

        notifications.show({
          title: 'Success',
          message: 'User created successfully',
          color: 'green',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
        closeModal()
      } else if (error) {
        console.log('error', d)
        notifications.show({
          title: 'Error',
          message: 'There was a problem creating the user. Please check the form carefully and try again.',
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      }
      setIsProcessing(false)
    },
    onError: error => {
      setIsProcessing(false)
      const description = (error as any).response?.data?.detail ?? 'An unexpected error occurred. Please try again.'
      notifications.show({
        title: 'Invitation Failed',
        message: description,
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  const previewEmailMutation = useMutation({
    mutationFn: api.previewInvite,
    onSuccess: data => {
      setEmailPreview(data)
      setPreviewModalOpen(true)
    },
    onError: error => {
      const description = (error as any).response?.data?.detail ?? 'Failed to generate email preview. Please try again.'
      notifications.show({
        title: 'Preview Failed',
        message: description,
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  // const createBulkUsersMutation = useMutation({
  //   mutationFn: api.createBulkUsers,
  //   onMutate: () => uiStore.showLoadingOverlay(),
  //   onSettled: () => uiStore.hideLoadingOverlay(),
  //   onSuccess: () => {
  //     api.queryClient.invalidateQueries({ queryKey: ['users', 'list'] })
  //     closeModal()
  //     notifications.show({
  //       title: 'Success',
  //       message: 'Bulk upload processing started',
  //       color: 'green',
  //       classNames: commonClasses.notificationFilled,
  //       position: 'bottom-left',
  //     })
  //   },
  //   onError: error => {
  //     console.error('error', error)
  //     notifications.show({
  //       title: 'Error',
  //       message: 'Failed to create users. Please try again.',
  //       color: 'red',
  //       classNames: commonClasses.notificationFilled,
  //       position: 'bottom-left',
  //     })
  //   },
  // })

  const handleSubmit = (values: UserFormValues) => {
    setIsProcessing(true)
    const trialEnabled = values.role === 'User' && !!values.enableTrial
    const trialDays = resolveTrialDays(isSwiAdmin, values.trialDays)
    createUserMutation.mutate({
      user_details: {
        email: values.email,
        name: values.name,
        password: values.password,
        sme: values.role === 'SME' || values.role === 'Admin',
        sponsor_admin: values.role === 'Admin',
        is_user_disabled: false,
        sms_mfa_enabled: false,
        existing_sponsor_for_user_confirmed: true,
        ...(trialEnabled ? { is_trial: true, trial_days: trialDays } : {}),
      },
      personal_message: values.personalMessage,
    })
  }

  const handlePreviewEmail = () => {
    const trialEnabled = form.values.role === 'User' && !!form.values.enableTrial
    const trialDays = resolveTrialDays(isSwiAdmin, form.values.trialDays)
    previewEmailMutation.mutate({
      user_details: {
        email: form.values.email,
        name: form.values.name,
        sme: form.values.role === 'SME' || form.values.role === 'Admin',
        sponsor_admin: form.values.role === 'Admin',
        is_user_disabled: false,
        sms_mfa_enabled: false,
        is_trial: trialEnabled,
        ...(trialEnabled ? { trial_days: trialDays } : {}),
      },
      personal_message: form.values.personalMessage,
    })
  }

  // const handleCsvUpload = (files: File[]) => {
  //   const file = files[0]
  //   if (file) {
  //     setUploadedFile(file)
  //   }
  // }

  // const handleRemoveFile = () => {
  //   setUploadedFile(null)
  // }

  // const handleBulkUpload = () => {
  //   if (!uploadedFile) return

  //   const formData = new FormData()
  //   formData.append('file', uploadedFile)

  //   createBulkUsersMutation.mutate({ file: uploadedFile })
  // }

  // const handleDownloadTemplate = () => {
  //   const link = document.createElement('a')
  //   link.href = '/example-template.csv'
  //   link.download = 'example-template.csv'
  //   document.body.appendChild(link)
  //   link.click()
  //   document.body.removeChild(link)
  // }

  return (
    <Tabs defaultValue="manual" styles={{ root: { paddingTop: 0 } }}>
      {/* <Tabs.List grow mb="md">
        <Tabs.Tab
          value="manual"
          leftSection={<IconUserPlus size={20} />}
          styles={{
            tab: {
              fontSize: '1rem',
              padding: '12px 16px',
            },
          }}>
          Single User
        </Tabs.Tab>
        <Tabs.Tab
          value="csv"
          leftSection={<IconFileSpreadsheet size={20} />}
          styles={{
            tab: {
              fontSize: '1rem',
              padding: '12px 16px',
            },
          }}>
          Bulk Upload
        </Tabs.Tab>
      </Tabs.List> */}

      {/* <Tabs.Panel value="manual"> */}
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="lg">
          <Group gap="sm" align="space-between">
            <TextInput
              required
              label="Email"
              placeholder="user@example.com"
              flex={1}
              {...form.getInputProps('email')}
            />
            {canSelectRole && (
              <Select
                required
                label="Role"
                data={[
                  { value: 'User', label: 'User' },
                  { value: 'SME', label: 'Expert' },
                  { value: 'Admin', label: 'Program Manager' },
                  // { value: 'SWI Admin', label: 'SWI Admin' },
                ]}
                {...form.getInputProps('role')}
                style={{ width: 200 }}
              />
            )}
          </Group>
          <TextInput label="Name" placeholder="John Smith" {...form.getInputProps('name')} />

          <Textarea
            label="Personal Message (Optional)"
            placeholder="Add a personal message to include in the invitation email..."
            rows={2}
            {...form.getInputProps('personalMessage')}
          />

          {form.values.role === 'User' && (
            <Stack gap={4}>
              <Checkbox
                label={`Enable ${TRIAL_DURATION_LABEL} trial`}
                {...form.getInputProps('enableTrial', { type: 'checkbox' })}
              />
              <Text size="xs" c="dimmed">
                This account will have full access for {TRIAL_DURATION_LABEL} after first login and then expire.
              </Text>
              {form.values.enableTrial && isSwiAdmin && (
                <NumberInput
                  mt="xs"
                  label="Trial duration (days)"
                  description={`SWI Admin only`}
                  min={MIN_TRIAL_DAYS}
                  max={MAX_TRIAL_DAYS}
                  allowDecimal={false}
                  allowNegative={false}
                  clampBehavior="strict"
                  {...form.getInputProps('trialDays')}
                />
              )}
            </Stack>
          )}

          {/* <TextInput
            type="password"
            label="Password"
            placeholder="(Optional)"
            style={{ flex: 1 }}
            {...form.getInputProps('password')}
          /> */}
          <Group justify="space-between" mt="xl">
            <Button
              variant="outline"
              leftSection={<IconEye size={16} />}
              onClick={handlePreviewEmail}
              loading={previewEmailMutation.isPending}
              disabled={isProcessing}>
              Preview Email
            </Button>
            <Group>
              <Button variant="light" onClick={closeModal} disabled={isProcessing}>
                Cancel
              </Button>
              <Button type="submit" loading={isProcessing} disabled={!!form.errors.email}>
                Invite {entityName}
              </Button>
            </Group>
          </Group>
        </Stack>
      </form>
      {/* </Tabs.Panel> */}

      {/* Bulk upload disabled for now */}
      {/* <Tabs.Panel value="csv">
        <Stack gap="lg">
          <Group gap={4}>
            <Text size="sm">Upload your CSV list to add up to</Text>
            <Text size="sm" fw={700}>
              500 users
            </Text>
            <Text size="sm">at once.</Text>
          </Group>

          <Dropzone
            onDrop={handleCsvUpload}
            accept={['.csv', '.xlsx']}
            styles={_ => ({
              root: {
                borderColor: theme.colors.primaryColors[6],
                backgroundColor: theme.colors.primaryColors[0],
                '&:hover': {
                  backgroundColor: theme.colors.primaryColors[2],
                },
              },
            })}>
            <Stack gap="xs" align="center" justify="center" h={200}>
              {uploadedFile ? (
                <>
                  <Group gap="xs" align="center">
                    <IconFileSpreadsheet size={32} stroke={1.5} color={theme.colors.primaryColors[6]} />
                    <Box>
                      <Text size="sm" fw={500}>
                        {uploadedFile.name}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {(uploadedFile.size / 1024).toFixed(1)} KB
                      </Text>
                    </Box>
                    <Button
                      variant="subtle"
                      color="red"
                      size="xs"
                      p={10}
                      aria-label="Remove uploaded file"
                      style={{
                        pointerEvents: 'auto',
                        backgroundColor: 'white',
                        position: 'relative',
                        top: -20,
                        right: -20,
                      }}
                      onClick={e => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleRemoveFile()
                      }}>
                      <IconX size={18} aria-hidden="true" />
                    </Button>
                  </Group>
                  <Text size="xs" c="dimmed" ta="center">
                    Drop a new file to replace
                  </Text>
                </>
              ) : (
                <>
                  <Dropzone.Accept>
                    <IconUpload size={32} stroke={1.5} color={theme.colors.primaryColors[6]} />
                  </Dropzone.Accept>
                  <Dropzone.Reject>
                    <IconUpload size={32} stroke={1.5} color={theme.colors.red[6]} />
                  </Dropzone.Reject>
                  <Dropzone.Idle>
                    <IconUpload size={32} stroke={1.5} color={theme.colors.primaryColors[6]} />
                  </Dropzone.Idle>

                  <Text ta="center" size="sm">
                    Click here or drag file to upload
                  </Text>
                  <Text size="xs" c="dimmed" ta="center">
                    Supported formats: CSV, Excel
                  </Text>
                </>
              )}
            </Stack>
          </Dropzone>

          <Box>
            <Group gap="xs">
              <Text size="sm" fw={500}>
                Required Fields:
              </Text>
              <Text size="sm" c="dimmed">
                email, role
              </Text>
            </Group>
          </Box>

          <Paper
            withBorder
            p="sm"
            bg="gray.0"
            component="button"
            onClick={handleDownloadTemplate}
            style={{
              width: '100%',
              cursor: 'pointer',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
            <Group gap="xs" align="center">
              <IconFileSpreadsheet size={24} color={theme.colors.green[6]} />
              <Box>
                <Text size="sm" fw={500}>
                  example-template.csv
                </Text>
                <Text size="xs" c="dimmed">
                  Download and fill this template
                </Text>
              </Box>
            </Group>
            <IconDownload size={20} color="gray" />
          </Paper>

          <Group justify="flex-end" mt="xl">
            <Button variant="light" onClick={closeModal}>
              Cancel
            </Button>
            <Button onClick={handleBulkUpload} loading={createBulkUsersMutation.isPending} disabled={!uploadedFile}>
              Upload and Process
            </Button>
          </Group>
        </Stack>
      </Tabs.Panel> */}

      {/* Email Preview Modal */}
      <EmailPreview opened={previewModalOpen} onClose={() => setPreviewModalOpen(false)} emailData={emailPreview} />
    </Tabs>
  )
}
